import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import ConfirmDelete from "@/components/ConfirmDelete";
import ResetAllData from "@/components/ResetAllData";
import { Plus, X, ShieldCheck, User, Eye, EyeOff, Pencil, Crown, IndianRupee } from "lucide-react";
import { toast, Toaster } from "sonner";

const PERM_LEVELS = ["none", "read", "write"];
const blankForm = () => ({ email: "", password: "", name: "", role: "operator", view_prices: false, permissions: {} });

export default function Users() {
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [reveal, setReveal] = useState({});

  const load = useCallback(() => Promise.all([api.get("/users"), api.get("/modules")]).then(([u, m]) => { setItems(u.data); setModules(m.data); }), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const perms = {}; modules.forEach((m) => { perms[m.key] = "none"; });
    setEditing(null); setForm({ ...blankForm(), permissions: perms }); setShowForm(true);
  };

  const openEdit = (u) => {
    const perms = {}; modules.forEach((m) => { perms[m.key] = u.permissions?.[m.key] || "none"; });
    setEditing(u);
    setForm({ email: u.email, password: "", name: u.name, role: u.role, view_prices: !!u.view_prices, permissions: perms });
    setShowForm(true);
  };

  const setPerm = (key, level) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: level } }));
  const setAll = (level) => setForm((f) => { const p = {}; modules.forEach((m) => { p[m.key] = level; }); return { ...f, permissions: p }; });

  const onSave = async () => {
    if (!form.name || (!editing && (!form.email || !form.password))) { toast.error("Name, email and password are required"); return; }
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, view_prices: form.view_prices, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const roleBadge = (role) => {
    if (role === "super_admin") return <span className="stat-pill bg-amber-100 text-amber-700"><Crown size={12} /> super admin</span>;
    if (role === "admin") return <span className="stat-pill bg-purple-100 text-purple-700"><ShieldCheck size={12} /> admin</span>;
    return <span className="stat-pill bg-blue-100 text-blue-700"><User size={12} /> {role}</span>;
  };

  const permSummary = (u) => {
    if (u.role === "super_admin") return "Full access (all tabs)";
    const w = []; const r = [];
    modules.forEach((m) => { const lv = u.permissions?.[m.key]; if (lv === "write") w.push(m.label); else if (lv === "read") r.push(m.label); });
    if (w.length === 0 && r.length === 0) return "No tabs assigned";
    return `${w.length} write · ${r.length} read-only`;
  };

  return (
    <Layout title="Users & Roles" subtitle="Create users, assign tab access (read/write) and price visibility"
      actions={<button data-testid="user-add-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Plus size={16} /> New User</button>}
    >
      <Toaster richColors position="top-right" />
      <div className="section-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr><th className="text-left py-3 px-4">Name</th><th className="text-left py-3 px-4">Email</th><th className="text-left py-3 px-4">Role</th><th className="text-left py-3 px-4">Password</th><th className="text-left py-3 px-4">Access</th><th className="text-center py-3 px-4">Prices</th><th className="text-right py-3 px-4">Actions</th></tr>
          </thead>
          <tbody data-testid="users-table-body">
            {items.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="py-2.5 px-4 font-medium"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-bold">{u.name?.[0] || "U"}</div>{u.name}</div></td>
                <td className="py-2.5 px-4 text-slate-600">{u.email}</td>
                <td className="py-2.5 px-4">{roleBadge(u.role)}</td>
                <td className="py-2.5 px-4">
                  {u.password ? (
                    <span className="flex items-center gap-2 font-mono text-xs">
                      <span data-testid={`user-pw-${u.id}`}>{reveal[u.id] ? u.password : "••••••••"}</span>
                      <button onClick={() => setReveal((r) => ({ ...r, [u.id]: !r[u.id] }))} className="text-slate-400 hover:text-slate-700">{reveal[u.id] ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                    </span>
                  ) : <span className="text-xs text-slate-400 italic">reset to view</span>}
                </td>
                <td className="py-2.5 px-4 text-slate-500 text-xs">{permSummary(u)}</td>
                <td className="py-2.5 px-4 text-center">{(u.role === "super_admin" || u.view_prices) ? <IndianRupee size={14} className="inline text-emerald-600" /> : <span className="text-slate-300 text-xs">hidden</span>}</td>
                <td className="py-2.5 px-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} className="text-slate-500 hover:text-blue-600"><Pencil size={15} /></button>
                    {u.role !== "super_admin" && <ConfirmDelete testId={`user-delete-${u.id}`} onConfirm={() => onDelete(u.id)} title="Delete user?" />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ResetAllData />

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="font-heading text-xl font-semibold">{editing ? `Edit · ${editing.name}` : "New User"}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Name</label><input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label><input data-testid="user-email" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Password {editing && <span className="text-slate-400 font-normal">(leave blank to keep)</span>}</label><input data-testid="user-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" placeholder={editing ? "••••••" : ""} /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Role label</label><input data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="operator / supervisor / admin…" /><p className="text-xs text-slate-400 mt-1">Custom labels allowed. Use "super_admin" only for full-control users.</p></div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
                <input data-testid="user-view-prices" type="checkbox" checked={form.view_prices} onChange={(e) => setForm({ ...form, view_prices: e.target.checked })} className="w-4 h-4" />
                <IndianRupee size={15} className="text-emerald-600" />
                <span className="text-sm text-slate-700">Can view prices & costs (inventory ₹, electrolysis cost, etc.)</span>
              </label>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-slate-700">Tab access</label>
                  <div className="flex gap-1 text-xs">
                    <button onClick={() => setAll("none")} className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200">All None</button>
                    <button onClick={() => setAll("read")} className="px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200">All Read</button>
                    <button onClick={() => setAll("write")} className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">All Write</button>
                  </div>
                </div>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {modules.map((m) => (
                    <div key={m.key} className="flex items-center justify-between px-3 py-2">
                      <span className="text-sm text-slate-700">{m.label}</span>
                      <div className="flex gap-1" data-testid={`perm-${m.key}`}>
                        {PERM_LEVELS.map((lv) => (
                          <button key={lv} data-testid={`perm-${m.key}-${lv}`} onClick={() => setPerm(m.key, lv)}
                            className={`px-3 py-1 rounded text-xs font-semibold capitalize transition-colors ${
                              form.permissions[m.key] === lv
                                ? (lv === "write" ? "bg-emerald-600 text-white" : lv === "read" ? "bg-blue-600 text-white" : "bg-slate-400 text-white")
                                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                            }`}>
                            {lv}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50 sticky bottom-0">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white">Cancel</button>
              <button data-testid="user-save-btn" onClick={onSave} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">{editing ? "Save changes" : "Create user"}</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
